import {Injectable} from '@angular/core';
import {ToastrService} from 'ngx-toastr';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {AskDialogComponent} from '../swiss-forms/ask-dialog/ask-dialog.component';
import {InputDialogComponent} from '../swiss-forms/input-dialog/input-dialog.component';
import {WaitDialogComponent} from '../swiss-forms/wait-dialog/wait-dialog.component';
import {FileNameDialogComponent} from '../swiss-forms/filename-dialog/filename-dialog.component';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject} from 'rxjs';
import {DialogInput, DialogWait, DialogYesNo, DialogYesNoWithList, InputDialogOptions, OutputDialogData} from '../models/dialog-models';

@Injectable({
    providedIn: 'root',
})
export class MessageBoxService {
    private matDialogRef?: MatDialogRef<any>;
    private userInputBlocked: BehaviorSubject<boolean> = new BehaviorSubject(false);

    constructor(private toastr: ToastrService,
                private dialog: MatDialog,
                private translate: TranslateService) {
    }

    public showSuccess(message: any, title: any, timeout: number = 3000): void {
        this.toastr.success(message, title, {
            positionClass: 'toast-top-full-width', timeOut: timeout, enableHtml: true,
        });
    }

    public showSuccessOK(message: any, title: any): Promise<any> {
        return new Promise((resolve) => {
            if (this.userInputBlocked.getValue()) {
                throw Error('user inputs are blocked by user');
            }
            const dialogOKData: DialogYesNo = {
                title, titleReplace: [], question: message, questionReplace: [], confirm: 'MAIN_APP_DIALOG.OK_DIALOG.confirm', cancel: '', dontShowCancel: true, dontShowTitle: false
            };

            const dialogRef = this.dialog.open(AskDialogComponent, {
                disableClose: false, data: dialogOKData, closeOnNavigation: true,
            });

            dialogRef.afterClosed().subscribe({
                next: (result) => {
                    if (result) {
                        resolve(result);
                    } else {
                        resolve(result);
                    }
                },
            });
        });
    }

    public showError(message: any, title: any): void {
        this.toastr.error(message, title, {enableHtml: true});
    }

    public showErrorByLanguageKey(messageKey: string, titleKey: string, msgReplace: string[] = [], titleReplace: string[] = []): void {
        let msg = '';
        let title = '';
        if (messageKey) {
            this.translate.get(messageKey).subscribe((message: string) => {
                msg = this.replaceText(message, msgReplace);
            });
        }
        if (titleKey) {
            this.translate.get(titleKey).subscribe((titleText: string) => {
                title = this.replaceText(titleText, titleReplace);
            });
        }

        this.showError(msg, title);
    }


    public showSuccessByLanguageKey(messageKey: string, titleKey: string, msgReplace: string[] = [], titleReplace: string[] = []): void {
        let msg = '';
        let title = '';
        if (messageKey) {
            this.translate.get(messageKey).subscribe((message: string) => {
                msg = this.replaceText(message, msgReplace);
            });
        }
        if (titleKey) {
            this.translate.get(titleKey).subscribe((titleText: string) => {
                title = this.replaceText(titleText, titleReplace);
            });
        }

        this.showSuccess(msg, title);
    }


    public showInfoByLangagueKey(messageKey: string, titleKey: string, msgReplace: string[] = [], titleReplace: string[] = []): void {
        let msg = '';
        let title = '';
        if (messageKey) {
            this.translate.get(messageKey).subscribe((message: string) => {
                msg = this.replaceText(message, msgReplace);
            });
        }
        if (titleKey) {
            this.translate.get(titleKey).subscribe((titleText: string) => {
                title = this.replaceText(titleText, titleReplace);
            });
        }

        this.showInfo(msg, title);
    }

    public showErrorOK(message: any, title: any, msgReplace: string[] = [], titleReplace: string[] = [], confirm = 'MAIN_APP_DIALOG.OK_DIALOG.confirm'): Promise<boolean> {
        return new Promise((resolve) => {
            if (this.userInputBlocked.getValue()) {
                throw Error('user inputs are blocked by user');
            }
            const dialogOKData: DialogYesNo = {
                title, titleReplace, question: message, questionReplace: msgReplace, confirm, cancel: '', dontShowCancel: true, dontShowTitle: false
            };

            const dialogRef = this.dialog.open(AskDialogComponent, {
                disableClose: true, data: dialogOKData, closeOnNavigation: true
            });

            dialogRef.afterClosed().subscribe({
                next: (result) => {
                    if (result) {
                        resolve(result);
                    } else {
                        resolve(result);
                    }
                },
            });
        });
    }

    public showInfo(message: any, title: any): void {
        this.toastr.info(message, title, {enableHtml: true});
    }

    public showWarning(message: any, title: any): void {
        this.toastr.warning(message, title, {enableHtml: true});
    }

    public startWaitingDialog(message: any, title: any): void {
        const dialogWait: DialogWait = {
            title, titleReplace: [], message, confirm: ''
        };

        this.matDialogRef = this.dialog.open(WaitDialogComponent, {
            disableClose: true, data: dialogWait,
        });
    }

    public stopWaitingDialog(): void {
        if (this.matDialogRef) {
            this.matDialogRef.close();
        }
    }

    public async askYesNoQuestion(message: any, title: any, msgReplace: string[] = [], titleReplace: string[] = [], dontShowCancel = false, confirm = 'MAIN_APP_DIALOG.confirm', cancel = 'MAIN_APP_DIALOG.cancel', dontShowTitle = false): Promise<boolean> {
        return new Promise((resolve) => {
            if (this.userInputBlocked.getValue()) {
                throw Error('user inputs are blocked by user');
            }
            const dialogYesNo: DialogYesNo = {
                title, question: message, questionReplace: msgReplace, titleReplace, dontShowCancel, confirm, cancel, dontShowTitle
            };

            const dialogRef = this.dialog.open(AskDialogComponent, {
                disableClose: false, closeOnNavigation: true, data: dialogYesNo, panelClass: ['swiss-soft-dialog', 'ask-dialog'],
            });

            dialogRef.afterClosed().subscribe({
                next: (result) => {
                    if (result) {
                        resolve(result);
                    } else {
                        resolve(false);
                    }
                },
            });
        });
    }

    public async askYesNoQuestionWithList(message: any, title: any, enumeration: string[], msgReplace: string[] = [], titleReplace: string[] = [], dontShowCancel = false, confirm = 'MAIN_APP_DIALOG.confirm', cancel = 'MAIN_APP_DIALOG.cancel', dontShowTitle = false): Promise<boolean> {
        return new Promise((resolve) => {
            if (this.userInputBlocked.getValue()) {
                throw Error('user inputs are blocked by user');
            }
            const dialogYesNo: DialogYesNoWithList = {
                title, question: message, enumeration, questionReplace: msgReplace, titleReplace, confirm, cancel, dontShowCancel, dontShowTitle
            };

            const dialogRef = this.dialog.open(AskDialogComponent, {
                disableClose: false, closeOnNavigation: true, data: dialogYesNo, panelClass: ['swiss-soft-dialog', 'ask-dialog', 'enumeration'],
            });

            dialogRef.afterClosed().subscribe({
                next: (result) => {
                    if (result) {
                        resolve(result);
                    } else {
                        resolve(result);
                    }
                },
            });
        });
    }

    public async showInputDialog<T>(title: string, fieldLabel: string, defaultValue: T, confirm = 'MAIN_APP_DIALOG.INPUT_DIALOG.save', cancel = 'MAIN_APP_DIALOG.INPUT_DIALOG.cancel', type = 'text', dontShowTitle = false, disableClose = false, options?: InputDialogOptions<T>): Promise<OutputDialogData<T>> {
        return new Promise((resolve) => {
            if (this.userInputBlocked.getValue()) {
                throw Error('user inputs are blocked by user');
            }
            const dialogInputData: DialogInput<T> = {
                title, titleReplace: [], label: fieldLabel, defaultValue, confirm, type, cancel, dontShowCancel: false, dontShowTitle, options
            };

            const dialogRef = this.dialog.open(InputDialogComponent<T>, {
                disableClose, data: dialogInputData,
            });

            dialogRef.afterClosed().subscribe({
                next: (result) => {
                    if (result) {
                        resolve(result);
                    } else {
                        resolve(result);
                    }
                },
            });
        });
    }

    public async showFileNameDialog(data): Promise<OutputDialogData<string>> {
        return new Promise((resolve) => {
            if (this.userInputBlocked.getValue()) {
                throw Error('user inputs are blocked by user');
            }
            const dialogRef = this.dialog.open(FileNameDialogComponent, {
                data,
            });

            dialogRef.afterClosed().subscribe({
                next: (result) => {
                    if (result) {
                        resolve(result);
                    } else {
                        resolve(result);
                    }
                },
            });
        });
    }

    public allowUserInputDialogs(): void {
        this.userInputBlocked.next(false);
    }

    public blockUserInputs(): void {
        this.userInputBlocked.next(true);
    }

    private replaceText(text: string, replacements: string[]): string {
        return text.replace(/%s/g, (): any => replacements.shift());
    }
}
